import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {deleteRecord, fetchRecords} from '../services/PricesService';
import {useState, useEffect} from 'react';
import {Price} from '../types';
import {columns} from '../tables/columns';
import {usePagination} from '../../../hooks/usePagination';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

const normalizeRecord = (record: Price) => {
    return Object.assign({}, record, {'unitAmount': (parseInt(record.unitAmount) / 100)});
};

export const usePrices = (productId?: string) => {
    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const pagination = usePagination();

    const pricesQuery = useQuery({
        queryKey: ['prices', productId, pagination.currentToken],
        queryFn: () => fetchRecords(productId ? {product: productId} : {}, pagination.pageSize, pagination.currentToken),
    });

    useEffect(() => {
        if (pricesQuery.data) pagination.setNextToken(pricesQuery.data.nextToken);
    }, [pricesQuery.data]);

    useEffect(() => {
        pagination.resetPagination();
    }, [productId]);

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Price deleted');
            queryClient.invalidateQueries({queryKey: ['prices']});
        },
        onError: notifyError,
    });

    const fetchRecordsAction = () => {
        queryClient.invalidateQueries({queryKey: ['prices']});
    };

    const deleteRecords = (ids?: string[]) => {
        if (ids && ids.length > 0) {
            deleteMutation.mutate(ids);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const rawItems: Price[] = pricesQuery.data?.items || [];

    const allItems = rawItems.map(normalizeRecord);

    const visibleItems = keyword
        ? allItems.filter(item => {
            const search = keyword.toLowerCase();
            return (
                item.id?.toLowerCase().includes(search) ||
                item.nickname?.toLowerCase().includes(search) ||
                item.currency?.toLowerCase().includes(search) ||
                item.product?.toLowerCase().includes(search)
            );
        })
        : allItems;

    return {
        items: allItems,
        visibleItems,
        loading: pricesQuery.isLoading,
        keyword,
        visibleCount: visibleItems.length,
        totalCount: allItems.length,
        columns,
        fetchRecords: fetchRecordsAction,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation,
        currentPage: pagination.currentPage,
        hasNext: pagination.hasNext,
        hasPrevious: pagination.hasPrevious,
        goToNextPage: pagination.goToNextPage,
        goToPreviousPage: pagination.goToPreviousPage,
    };
};
